package com.sweetlime.dungeonamigo.presentation.features.character

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.sweetlime.dungeonamigo.model.Character
import com.sweetlime.dungeonamigo.model.CharacterCalculator
import com.sweetlime.dungeonamigo.presentation.ui.components.AvatarVariantPickerSheet
import com.sweetlime.dungeonamigo.presentation.ui.components.CharacterPortrait
import com.sweetlime.dungeonamigo.presentation.ui.components.PortraitPickerSheet
import com.sweetlime.dungeonamigo.presentation.ui.components.PortraitSize
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import com.sweetlime.dungeonamigo.presentation.ui.theme.classColorFor
import com.sweetlime.dungeonamigo.presentation.ui.theme.raceColorFor
import com.sweetlime.dungeonamigo.presentation.ui.theme.pixelFont
import com.sweetlime.dungeonamigo.presentation.ui.theme.silkFont
import org.koin.compose.koinInject

private data class RaceInfo(val name: String, val traits: String, val subraces: List<String> = emptyList())
private data class ClassInfo(val name: String, val hitDie: String)

private val SRD_RACES = listOf(
    RaceInfo("Human",      "Versatile — +1 to every ability"),
    RaceInfo("Elf",        "Darkvision · Trance · Keen Senses",    listOf("High Elf", "Wood Elf", "Dark Elf")),
    RaceInfo("Dwarf",      "Darkvision · Resilience · Stonecunning", listOf("Hill Dwarf", "Mountain Dwarf")),
    RaceInfo("Halfling",   "Lucky · Brave · Halfling Nimbleness",  listOf("Lightfoot", "Stout")),
    RaceInfo("Gnome",      "Darkvision · Gnome Cunning",           listOf("Forest Gnome", "Rock Gnome")),
    RaceInfo("Half-Elf",   "Darkvision · Fey Ancestry · +2 skills"),
    RaceInfo("Half-Orc",   "Darkvision · Relentless · Savage Atk"),
    RaceInfo("Tiefling",   "Darkvision · Hellish Res. · Infernal"),
    RaceInfo("Dragonborn", "Draconic Ancestry · Breath Weapon"),
)

private val SRD_CLASSES = CharacterCalculator.CLASS_HIT_DICE.entries.map { (name, die) ->
    ClassInfo(name, die)
}

private val BACKGROUNDS = listOf(
    "Acolyte", "Charlatan", "Criminal", "Entertainer", "Folk Hero",
    "Guild Artisan", "Hermit", "Noble", "Outlander", "Sage", "Soldier", "Urchin",
)

private val STANDARD_ARRAY = listOf(15, 14, 13, 12, 10, 8)
private val ABILITY_LABELS = listOf("STR", "DEX", "CON", "INT", "WIS", "CHA")

object CharacterCreationScreen : Screen {
    @Composable
    override fun Content() {
        val navigator  = LocalNavigator.currentOrThrow
        val vm: CharacterViewModel = koinInject()
        val step       by vm.creationStep.collectAsState()
        val draft      by vm.creationDraft.collectAsState()
        val isCreating by vm.isCreating.collectAsState()
        val userTier   by vm.userTier.collectAsState()
        var errorMsg   by remember { mutableStateOf<String?>(null) }
        val pf = pixelFont()

        Column(
            Modifier
                .fillMaxSize()
                .background(BrandColors.Background)
                .windowInsetsPadding(WindowInsets.systemBars),
        ) {
            // ── Top bar ──────────────────────────────────────────────────────
            Row(
                Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 4.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                IconButton(onClick = { if (step.ordinal == 0) navigator.pop() else vm.previousStep() }) {
                    Icon(Icons.Filled.ArrowBack, "Back", tint = BrandColors.SlateLight)
                }
                Text(
                    text       = "Create Character",
                    color      = BrandColors.TextPrimary,
                    fontFamily = pf,
                    fontWeight = FontWeight.Bold,
                    fontSize   = 23.sp,
                    modifier   = Modifier.weight(1f).padding(start = 4.dp),
                )
            }

            // ── Step indicator (squares) ─────────────────────────────────────
            Row(
                Modifier.fillMaxWidth().padding(horizontal = 24.dp, vertical = 8.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.CenterHorizontally),
            ) {
                CreationStep.entries.forEach { s ->
                    val isCurrent   = s == step
                    val isCompleted = s.ordinal < step.ordinal
                    Box(
                        modifier = Modifier
                            .size(if (isCurrent) 9.dp else 7.dp)
                            .clip(RoundedCornerShape(2.dp))
                            .background(
                                when {
                                    isCurrent   -> BrandColors.Parchment
                                    isCompleted -> BrandColors.ParchmentDim
                                    else        -> BrandColors.Slate.copy(alpha = 0.4f)
                                }
                            ),
                    )
                }
            }

            // ── Step subtitle ────────────────────────────────────────────────
            Row(
                Modifier.padding(horizontal = 24.dp, vertical = 4.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Box(
                    Modifier
                        .size(7.dp)
                        .clip(RoundedCornerShape(1.dp))
                        .background(BrandColors.Parchment),
                )
                Text(
                    text       = when (step) {
                        CreationStep.RACE      -> "Choose Your Race"
                        CreationStep.CLASS     -> "Choose Your Class"
                        CreationStep.ABILITIES -> "Set Ability Scores"
                        CreationStep.DETAILS   -> "Fill in Details"
                        CreationStep.REVIEW    -> "Review & Create"
                    },
                    color      = BrandColors.Parchment,
                    fontFamily = pf,
                    fontWeight = FontWeight.Bold,
                    fontSize   = 17.sp,
                )
            }
            HorizontalDivider(color = BrandColors.Slate.copy(alpha = 0.2f), modifier = Modifier.padding(horizontal = 18.dp))

            // ── Step content ─────────────────────────────────────────────────
            Box(Modifier.weight(1f)) {
                when (step) {
                    CreationStep.RACE      -> RaceStep(draft, onSelect = { race, sub -> vm.updateDraftRace(race, sub) })
                    CreationStep.CLASS     -> ClassStep(draft, onSelect = { cls -> vm.updateDraftClass(cls) })
                    CreationStep.ABILITIES -> AbilitiesStep(draft, onUpdate = { s, d, c, i, w, ch -> vm.updateDraftAbilities(s, d, c, i, w, ch) })
                    CreationStep.DETAILS   -> DetailsStep(draft, onUpdate = { n, bg, al, lv, bs -> vm.updateDraftDetails(n, bg, al, lv, bs) })
                    CreationStep.REVIEW    -> ReviewStep(
                        draft = draft,
                        userTier = userTier,
                        onPortraitColorsChanged = { primary, secondary ->
                            vm.updateDraftPortraitColors(primary, secondary)
                        },
                        onAvatarVariantChanged = { variant ->
                            vm.updateDraftAvatarVariant(variant)
                        },
                    )
                }
            }

            errorMsg?.let {
                Text(
                    text     = it,
                    color    = BrandColors.Error,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(horizontal = 24.dp, vertical = 4.dp),
                )
            }

            // ── Footer buttons ───────────────────────────────────────────────
            HorizontalDivider(color = BrandColors.Slate.copy(alpha = 0.2f))
            Box(
                Modifier
                    .fillMaxWidth()
                    .background(BrandColors.Background.copy(alpha = 0.95f))
                    .padding(horizontal = 18.dp, vertical = 14.dp),
            ) {
                if (step != CreationStep.REVIEW) {
                    val canAdvance = when (step) {
                        CreationStep.RACE      -> draft.race.isNotBlank()
                        CreationStep.CLASS     -> draft.characterClass.isNotBlank()
                        CreationStep.ABILITIES -> true
                        CreationStep.DETAILS   -> draft.name.isNotBlank()
                        else                   -> true
                    }
                    PixelButton(
                        text     = "Next",
                        enabled  = canAdvance,
                        accent   = BrandColors.Ember,
                        modifier = Modifier.fillMaxWidth(),
                        onClick  = { errorMsg = null; vm.nextStep() },
                    )
                } else {
                    PixelButton(
                        text     = "Create Character",
                        enabled  = !isCreating && draft.name.isNotBlank(),
                        accent   = BrandColors.Parchment,
                        textColor = BrandColors.Background,
                        modifier = Modifier.fillMaxWidth(),
                        onClick  = {
                            errorMsg = null
                            vm.createCharacter(
                                onSuccess = { id -> navigator.replace(CharacterSheetScreen(id)) },
                                onError   = { msg -> errorMsg = msg },
                            )
                        },
                        loading  = isCreating,
                    )
                }
            }
        }
    }
}

// ── Pixel-depth button ────────────────────────────────────────────────────────
@Composable
private fun PixelButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    accent: Color = BrandColors.Ember,
    textColor: Color = BrandColors.TextPrimary,
    enabled: Boolean = true,
    loading: Boolean = false,
) {
    val pf = pixelFont()
    val shape = RoundedCornerShape(10.dp)
    Button(
        onClick  = onClick,
        enabled  = enabled,
        modifier = modifier.height(52.dp),
        shape    = shape,
        colors   = ButtonDefaults.buttonColors(
            containerColor         = accent,
            disabledContainerColor = BrandColors.Stone,
        ),
        elevation = ButtonDefaults.buttonElevation(defaultElevation = 4.dp),
    ) {
        if (loading) {
            CircularProgressIndicator(Modifier.size(20.dp), color = textColor, strokeWidth = 2.dp)
        } else {
            Text(
                text       = text,
                color      = if (enabled) textColor else BrandColors.IvoryDim,
                fontFamily = pf,
                fontWeight = FontWeight.Bold,
                fontSize   = 15.sp,
            )
        }
    }
}

// ── Accent icon tile (race / class) ──────────────────────────────────────────
@Composable
private fun AccentIconTile(
    initial: String,
    accent: Color,
    size: Dp = 46.dp,
) {
    val pf = pixelFont()
    Box(
        modifier = Modifier
            .size(size)
            .clip(RoundedCornerShape(11.dp))
            .background(Brush.verticalGradient(listOf(accent.copy(alpha = 0.26f), BrandColors.Stone)))
            .border(1.5.dp, accent.copy(alpha = 0.6f), RoundedCornerShape(11.dp)),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text       = initial.take(1).uppercase(),
            color      = accent,
            fontFamily = pf,
            fontWeight = FontWeight.Bold,
            fontSize   = (size.value * 0.4f).sp,
        )
    }
}

// ── Step 1: Race ─────────────────────────────────────────────────────────────
@Composable
private fun RaceStep(draft: Character, onSelect: (String, String) -> Unit) {
    var selectedRace    by remember(draft.race) { mutableStateOf(draft.race) }
    var selectedSubrace by remember(draft.subrace) { mutableStateOf(draft.subrace) }
    val raceInfo = SRD_RACES.find { it.name == selectedRace }
    val pf = pixelFont()

    Column(Modifier.fillMaxSize()) {
        LazyVerticalGrid(
            columns               = GridCells.Fixed(3),
            modifier              = Modifier.weight(1f),
            contentPadding        = PaddingValues(18.dp),
            verticalArrangement   = Arrangement.spacedBy(10.dp),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            items(SRD_RACES) { race ->
                val selected = race.name == selectedRace
                val accent   = raceColorFor(race.name)
                Column(
                    modifier = Modifier
                        .clip(RoundedCornerShape(14.dp))
                        .background(
                            if (selected)
                                Brush.linearGradient(listOf(accent.copy(alpha = 0.15f), BrandColors.Stone))
                            else
                                Brush.linearGradient(listOf(BrandColors.Stone, BrandColors.Stone))
                        )
                        .border(
                            width = if (selected) 1.5.dp else 1.dp,
                            color = if (selected) accent else BrandColors.Slate.copy(alpha = 0.3f),
                            shape = RoundedCornerShape(14.dp),
                        )
                        .clickable {
                            selectedRace    = race.name
                            selectedSubrace = ""
                            onSelect(race.name, "")
                        }
                        .padding(10.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    AccentIconTile(initial = race.name, accent = accent)
                    Text(
                        text       = race.name,
                        color      = BrandColors.TextPrimary,
                        fontFamily = pf,
                        fontSize   = 12.sp,
                        fontWeight = FontWeight.Bold,
                    )
                    Text(
                        text     = race.traits,
                        color    = BrandColors.IvoryDim,
                        fontSize = 9.sp,
                        maxLines = 2,
                    )
                }
            }
        }

        if (raceInfo != null && raceInfo.subraces.isNotEmpty()) {
            val sf = silkFont()
            Column(Modifier.padding(horizontal = 18.dp, vertical = 10.dp)) {
                Text("SUBRACE", color = BrandColors.IvoryDim, fontFamily = sf, fontSize = 9.sp)
                Spacer(Modifier.height(8.dp))
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    raceInfo.subraces.forEach { sub ->
                        val isSelected = sub == selectedSubrace
                        val accent = raceColorFor(selectedRace)
                        FilterChip(
                            selected = isSelected,
                            onClick  = { selectedSubrace = sub; onSelect(selectedRace, sub) },
                            label    = { Text(sub, fontSize = 12.sp) },
                            colors   = FilterChipDefaults.filterChipColors(
                                selectedContainerColor = accent.copy(alpha = 0.2f),
                                selectedLabelColor     = accent,
                                containerColor         = BrandColors.Surface,
                                labelColor             = BrandColors.TextSecondary,
                            ),
                            border = FilterChipDefaults.filterChipBorder(
                                enabled          = true,
                                selected         = isSelected,
                                selectedBorderColor = accent,
                                borderColor      = BrandColors.Slate.copy(alpha = 0.3f),
                            ),
                        )
                    }
                }
            }
        }
    }
}

// ── Step 2: Class ─────────────────────────────────────────────────────────────
@Composable
private fun ClassStep(draft: Character, onSelect: (String) -> Unit) {
    var selectedClass by remember(draft.characterClass) { mutableStateOf(draft.characterClass) }
    val pf = pixelFont()

    LazyVerticalGrid(
        columns               = GridCells.Fixed(3),
        modifier              = Modifier.fillMaxSize(),
        contentPadding        = PaddingValues(18.dp),
        verticalArrangement   = Arrangement.spacedBy(10.dp),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        items(SRD_CLASSES) { cls ->
            val selected = cls.name == selectedClass
            val accent   = classColorFor(cls.name)
            Column(
                modifier = Modifier
                    .clip(RoundedCornerShape(14.dp))
                    .background(
                        if (selected)
                            Brush.linearGradient(listOf(accent.copy(alpha = 0.15f), BrandColors.Stone))
                        else
                            Brush.linearGradient(listOf(BrandColors.Stone, BrandColors.Stone))
                    )
                    .border(
                        width = if (selected) 1.5.dp else 1.dp,
                        color = if (selected) accent else BrandColors.Slate.copy(alpha = 0.3f),
                        shape = RoundedCornerShape(14.dp),
                    )
                    .clickable { selectedClass = cls.name; onSelect(cls.name) }
                    .padding(10.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                AccentIconTile(initial = cls.name, accent = accent)
                Text(
                    text       = cls.name,
                    color      = BrandColors.TextPrimary,
                    fontFamily = pf,
                    fontSize   = 12.sp,
                    fontWeight = FontWeight.Bold,
                )
                // Hit-die badge — pill with SpellBlue tint
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(50.dp))
                        .background(BrandColors.SpellBlue.copy(alpha = 0.11f))
                        .border(1.dp, BrandColors.SpellBlue.copy(alpha = 0.33f), RoundedCornerShape(50.dp))
                        .padding(horizontal = 8.dp, vertical = 2.dp),
                ) {
                    Text(
                        text       = cls.hitDie,
                        color      = BrandColors.SpellBlue,
                        fontFamily = pf,
                        fontSize   = 11.sp,
                    )
                }
            }
        }
    }
}

// ── Step 3: Abilities ─────────────────────────────────────────────────────────
@Composable
private fun AbilitiesStep(
    draft: Character,
    onUpdate: (Int, Int, Int, Int, Int, Int) -> Unit,
) {
    val currentValues = listOf(
        draft.strength, draft.dexterity, draft.constitution,
        draft.intelligence, draft.wisdom, draft.charisma,
    )
    val assignments = remember {
        mutableStateListOf<Int?>(*currentValues.map { if (it == 10) null else it }.toTypedArray())
    }
    var pool by remember {
        val used      = assignments.filterNotNull().toMutableList()
        val remaining = STANDARD_ARRAY.toMutableList()
        used.forEach { remaining.remove(it) }
        mutableStateOf<List<Int>>(remaining)
    }
    var selectedPoolValue by remember { mutableStateOf<Int?>(null) }
    var isRolled          by remember { mutableStateOf(false) }
    val pf = pixelFont()
    val sf = silkFont()

    fun notifyUpdate() {
        val vals = assignments.map { it ?: 10 }
        onUpdate(vals[0], vals[1], vals[2], vals[3], vals[4], vals[5])
    }
    fun rollAll() {
        val rolled = List(6) {
            val dice = List(4) { (1..6).random() }
            dice.sum() - dice.min()
        }
        for (i in assignments.indices) assignments[i] = rolled[i]
        pool     = emptyList()
        isRolled = true
        notifyUpdate()
    }
    fun resetToStandard() {
        for (i in assignments.indices) assignments[i] = null
        pool             = STANDARD_ARRAY.toList()
        selectedPoolValue = null
        isRolled         = false
        notifyUpdate()
    }

    Column(
        Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = 18.dp, vertical = 16.dp),
    ) {
        // Helper line
        Text(
            text  = if (isRolled) "Rolled scores — tap a stat to re-assign" else "Standard array — tap a value, then tap a stat to assign it.",
            color = BrandColors.IvoryDim,
            fontSize = 12.sp,
        )
        Spacer(Modifier.height(12.dp))

        // Value chips
        if (!isRolled) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier.fillMaxWidth(),
            ) {
                pool.sorted().reversed().forEach { value ->
                    val isSelected = value == selectedPoolValue
                    Box(
                        modifier = Modifier
                            .height(52.dp)
                            .widthIn(min = 48.dp)
                            .clip(RoundedCornerShape(8.dp))
                            .background(
                                if (isSelected) BrandColors.Parchment.copy(alpha = 0.15f) else BrandColors.StoneLight
                            )
                            .border(
                                width = if (isSelected) 1.5.dp else 0.dp,
                                color = if (isSelected) BrandColors.Parchment else Color.Transparent,
                                shape = RoundedCornerShape(8.dp),
                            )
                            .clickable { selectedPoolValue = if (isSelected) null else value }
                            .padding(horizontal = 10.dp),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(
                            text       = "$value",
                            color      = if (isSelected) BrandColors.Parchment else BrandColors.TextPrimary,
                            fontFamily = pf,
                            fontWeight = FontWeight.Bold,
                            fontSize   = 21.sp,
                        )
                    }
                }
            }
            Spacer(Modifier.height(12.dp))
        }

        // Stat rows
        ABILITY_LABELS.forEachIndexed { i, label ->
            val assignedValue = assignments[i]
            val mod           = CharacterCalculator.modifier(assignedValue ?: 10)
            val readyHint     = selectedPoolValue != null && assignedValue == null && !isRolled
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(54.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .background(BrandColors.Surface)
                    .border(
                        width = if (readyHint) 1.dp else 0.dp,
                        color = if (readyHint) BrandColors.Parchment.copy(alpha = 0.4f) else Color.Transparent,
                        shape = RoundedCornerShape(12.dp),
                    )
                    .clickable {
                        val sel = selectedPoolValue
                        if (sel != null && !isRolled) {
                            val oldVal  = assignments[i]
                            assignments[i] = sel
                            val newPool = pool.toMutableList().also { it.remove(sel) }
                            if (oldVal != null) newPool.add(oldVal)
                            pool              = newPool.sorted().reversed()
                            selectedPoolValue = null
                            notifyUpdate()
                        } else if (assignedValue != null && sel == null && !isRolled) {
                            pool              = (pool + assignedValue).sorted().reversed()
                            assignments[i]    = null
                            notifyUpdate()
                        }
                    }
                    .padding(horizontal = 16.dp),
                verticalAlignment     = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                Text(label, color = BrandColors.IvoryDim, fontFamily = sf, fontWeight = FontWeight.Bold, fontSize = 13.sp)
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(
                        text       = if (assignedValue != null) "$assignedValue" else "—",
                        color      = if (assignedValue != null) BrandColors.Parchment else BrandColors.IvoryDim,
                        fontFamily = pf,
                        fontWeight = FontWeight.Bold,
                        fontSize   = 24.sp,
                    )
                    if (assignedValue != null) {
                        Text(
                            text     = CharacterCalculator.modifierString(assignedValue),
                            color    = if (mod >= 0) BrandColors.Venom else BrandColors.EmberDim,
                            fontFamily = pf,
                            fontSize = 14.sp,
                        )
                    }
                }
            }
            Spacer(Modifier.height(6.dp))
        }

        Spacer(Modifier.height(16.dp))

        // Action buttons
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            OutlinedButton(
                onClick  = { rollAll() },
                modifier = Modifier.weight(1f),
                border   = BorderStroke(1.dp, BrandColors.SpellBlue.copy(alpha = 0.5f)),
                colors   = ButtonDefaults.outlinedButtonColors(contentColor = BrandColors.SpellBlue),
            ) {
                Text("Roll 4d6 Drop Lowest", color = BrandColors.SpellBlue, fontSize = 12.sp)
            }
            OutlinedButton(
                onClick  = { resetToStandard() },
                modifier = Modifier.wrapContentWidth(),
                border   = BorderStroke(1.dp, BrandColors.Slate.copy(alpha = 0.5f)),
                colors   = ButtonDefaults.outlinedButtonColors(contentColor = BrandColors.IvoryDim),
            ) {
                Text("Reset", color = BrandColors.IvoryDim, fontSize = 12.sp)
            }
        }
    }
}

// ── Step 4: Details ───────────────────────────────────────────────────────────
@Composable
private fun DetailsStep(
    draft: Character,
    onUpdate: (String, String, String, Int, String) -> Unit,
) {
    var name       by remember(draft.name) { mutableStateOf(draft.name) }
    var background by remember(draft.background) { mutableStateOf(draft.background) }
    var alignment  by remember(draft.alignment) { mutableStateOf(draft.alignment) }
    var level      by remember(draft.level) { mutableStateOf(draft.level) }
    var backstory  by remember(draft.backstory) { mutableStateOf(draft.backstory) }
    var showBgMenu by remember { mutableStateOf(false) }
    val sf = silkFont()
    val pf = pixelFont()
    fun notify() = onUpdate(name, background, alignment, level, backstory)

    Column(
        Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = 18.dp, vertical = 16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        // Character Name
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text("CHARACTER NAME", color = BrandColors.IvoryDim, fontFamily = sf, fontSize = 9.sp)
            OutlinedTextField(
                value         = name,
                onValueChange = { if (it.length <= 24) { name = it; notify() } },
                modifier      = Modifier.fillMaxWidth().height(52.dp),
                singleLine    = true,
                placeholder   = { Text("Name your hero…", color = BrandColors.IvoryDim, fontSize = 14.sp) },
                colors        = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor   = BrandColors.Parchment,
                    unfocusedBorderColor = BrandColors.SurfaceLight,
                    focusedTextColor     = BrandColors.TextPrimary,
                    unfocusedTextColor   = BrandColors.TextPrimary,
                    cursorColor          = BrandColors.Parchment,
                ),
            )
        }

        // Background dropdown
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text("BACKGROUND", color = BrandColors.IvoryDim, fontFamily = sf, fontSize = 9.sp)
            Box {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(52.dp)
                        .clip(RoundedCornerShape(8.dp))
                        .background(BrandColors.StoneLight)
                        .border(1.dp, BrandColors.Slate.copy(alpha = 0.5f), RoundedCornerShape(8.dp))
                        .clickable { showBgMenu = true }
                        .padding(horizontal = 16.dp),
                    contentAlignment = Alignment.CenterStart,
                ) {
                    Text(
                        text  = background.ifBlank { "Select background…" },
                        color = if (background.isBlank()) BrandColors.IvoryDim else BrandColors.Parchment,
                        fontSize = 14.sp,
                    )
                }
                DropdownMenu(
                    expanded         = showBgMenu,
                    onDismissRequest = { showBgMenu = false },
                    modifier         = Modifier.background(BrandColors.Stone),
                ) {
                    BACKGROUNDS.forEach { bg ->
                        DropdownMenuItem(
                            text    = { Text(bg, color = BrandColors.TextPrimary) },
                            onClick = { background = bg; notify(); showBgMenu = false },
                        )
                    }
                }
            }
        }

        // Alignment 3×3 grid
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text("ALIGNMENT", color = BrandColors.IvoryDim, fontFamily = sf, fontSize = 9.sp)
            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                CharacterCalculator.ALIGNMENTS.chunked(3).forEach { row ->
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                        row.forEach { al ->
                            val isSelected = al == alignment
                            Box(
                                modifier = Modifier
                                    .weight(1f)
                                    .height(42.dp)
                                    .clip(RoundedCornerShape(8.dp))
                                    .background(
                                        if (isSelected) BrandColors.Ember.copy(alpha = 0.12f)
                                        else BrandColors.Surface
                                    )
                                    .border(
                                        width = if (isSelected) 1.dp else 0.dp,
                                        color = if (isSelected) BrandColors.Ember else Color.Transparent,
                                        shape = RoundedCornerShape(8.dp),
                                    )
                                    .clickable { alignment = if (al == alignment) "" else al; notify() },
                                contentAlignment = Alignment.Center,
                            ) {
                                Text(
                                    text       = al,
                                    color      = if (isSelected) BrandColors.TextPrimary else BrandColors.IvoryDim,
                                    fontSize   = 10.5.sp,
                                    fontWeight = if (isSelected) FontWeight.SemiBold else FontWeight.Normal,
                                )
                            }
                        }
                    }
                }
            }
        }

        // Starting Level stepper
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text("STARTING LEVEL", color = BrandColors.IvoryDim, fontFamily = sf, fontSize = 9.sp)
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                // Minus — Ember tint
                Box(
                    modifier = Modifier
                        .size(40.dp)
                        .clip(RoundedCornerShape(9.dp))
                        .background(BrandColors.Ember.copy(alpha = 0.15f))
                        .border(1.dp, BrandColors.Ember.copy(alpha = 0.4f), RoundedCornerShape(9.dp))
                        .clickable { if (level > 1) { level--; notify() } },
                    contentAlignment = Alignment.Center,
                ) {
                    Text("−", color = BrandColors.Ember, fontSize = 20.sp, fontWeight = FontWeight.Bold)
                }
                Text("$level", color = BrandColors.TextPrimary, fontFamily = pf, fontSize = 24.sp, fontWeight = FontWeight.Bold)
                // Plus — Venom tint
                Box(
                    modifier = Modifier
                        .size(40.dp)
                        .clip(RoundedCornerShape(9.dp))
                        .background(BrandColors.Venom.copy(alpha = 0.15f))
                        .border(1.dp, BrandColors.Venom.copy(alpha = 0.4f), RoundedCornerShape(9.dp))
                        .clickable { if (level < 20) { level++; notify() } },
                    contentAlignment = Alignment.Center,
                ) {
                    Text("+", color = BrandColors.Venom, fontSize = 20.sp, fontWeight = FontWeight.Bold)
                }
            }
        }

        // Backstory
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text("BACKSTORY (OPTIONAL)", color = BrandColors.IvoryDim, fontFamily = sf, fontSize = 9.sp)
            OutlinedTextField(
                value         = backstory,
                onValueChange = { backstory = it; notify() },
                modifier      = Modifier.fillMaxWidth().heightIn(min = 96.dp),
                minLines      = 4,
                colors        = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor   = BrandColors.Parchment,
                    unfocusedBorderColor = BrandColors.SurfaceLight,
                    focusedTextColor     = BrandColors.TextPrimary,
                    unfocusedTextColor   = BrandColors.TextPrimary,
                    cursorColor          = BrandColors.Parchment,
                ),
            )
        }
    }
}

// ── Step 5: Review ────────────────────────────────────────────────────────────
@Composable
private fun ReviewStep(
    draft: Character,
    userTier: String = "free",
    onPortraitColorsChanged: (Long, Long) -> Unit = { _, _ -> },
    onAvatarVariantChanged: (String) -> Unit = {},
) {
    var showPortraitPicker by remember { mutableStateOf(false) }
    var showVariantPicker  by remember { mutableStateOf(false) }

    if (showPortraitPicker) {
        PortraitPickerSheet(
            characterClass   = draft.characterClass,
            initialPrimary   = draft.portraitPrimaryColor,
            initialSecondary = draft.portraitSecondaryColor,
            onSave           = onPortraitColorsChanged,
            onDismiss        = { showPortraitPicker = false },
        )
    }
    if (showVariantPicker) {
        AvatarVariantPickerSheet(
            race            = draft.race,
            characterClass  = draft.characterClass,
            currentAvatarId = draft.avatarId,
            onSave          = { variantAvatarId ->
                onAvatarVariantChanged(variantAvatarId)
                showVariantPicker = false
            },
            onDismiss = { showVariantPicker = false },
        )
    }

    val hitDieName = CharacterCalculator.CLASS_HIT_DICE[draft.characterClass] ?: "d8"
    val hitDieSize = hitDieName.removePrefix("d").toIntOrNull() ?: 8
    val conMod     = CharacterCalculator.modifier(draft.constitution)
    val dexMod     = CharacterCalculator.modifier(draft.dexterity)
    val maxHp      = maxOf(1, hitDieSize + conMod)

    LazyColumn(
        modifier       = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(horizontal = 18.dp, vertical = 16.dp),
    ) {
        // Portrait + color pickers
        item {
            Column(
                modifier            = Modifier.fillMaxWidth().padding(bottom = 16.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                CharacterPortrait(
                    characterClass = draft.characterClass,
                    avatarId       = draft.avatarId,
                    primaryColor   = Color(draft.portraitPrimaryColor),
                    secondaryColor = Color(draft.portraitSecondaryColor),
                    size           = PortraitSize.LARGE,
                )
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedButton(
                        onClick = { showPortraitPicker = true },
                        border  = BorderStroke(1.dp, BrandColors.Parchment.copy(alpha = 0.5f)),
                        colors  = ButtonDefaults.outlinedButtonColors(contentColor = BrandColors.Parchment),
                        shape   = RoundedCornerShape(8.dp),
                    ) {
                        Text("Colors", fontSize = 13.sp)
                    }
                    if (userTier == "plus") {
                        OutlinedButton(
                            onClick = { showVariantPicker = true },
                            border  = BorderStroke(1.dp, BrandColors.SpellBlue.copy(alpha = 0.7f)),
                            colors  = ButtonDefaults.outlinedButtonColors(contentColor = BrandColors.SpellBlue),
                            shape   = RoundedCornerShape(8.dp),
                        ) {
                            Text("✦ Variant", fontSize = 13.sp)
                        }
                    }
                }
            }
        }

        // Identity card
        item {
            ReviewSection("Identity") {
                ReviewRow("Name",       draft.name.ifBlank { "—" })
                ReviewRow("Race",       buildString {
                    append(draft.race.ifBlank { "—" })
                    if (draft.subrace.isNotBlank()) append(" (${draft.subrace})")
                })
                ReviewRow("Class",      draft.characterClass.ifBlank { "—" })
                ReviewRow("Level",      "${draft.level}", valueColor = BrandColors.Parchment)
                ReviewRow("Background", draft.background.ifBlank { "—" })
                ReviewRow("Alignment",  draft.alignment.ifBlank { "—" })
            }
        }

        // Ability Scores
        item {
            ReviewSection("Ability Scores") {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceEvenly) {
                    listOf(
                        "STR" to draft.strength, "DEX" to draft.dexterity, "CON" to draft.constitution,
                        "INT" to draft.intelligence, "WIS" to draft.wisdom, "CHA" to draft.charisma,
                    ).forEach { (label, score) ->
                        val mod = CharacterCalculator.modifier(score)
                        AbilityScoreColumn(label = label, score = score, mod = mod)
                    }
                }
            }
        }

        // Combat auto-calculated
        item {
            ReviewSection("Combat — Auto-Calculated") {
                ReviewRow("Max HP",          "$maxHp  (${hitDieSize}${if (conMod >= 0) "+$conMod" else "$conMod"})", valueColor = BrandColors.Ember)
                ReviewRow("Armor Class",     "${10 + dexMod}", valueColor = BrandColors.SpellBlue)
                ReviewRow("Initiative",      CharacterCalculator.modifierString(draft.dexterity))
                ReviewRow("Speed",           "${CharacterCalculator.raceSpeed(draft.race)} ft.")
                ReviewRow("Hit Dice",        "${draft.level}$hitDieName")
                ReviewRow("Proficiency",     "+${CharacterCalculator.proficiencyBonus(draft.level)}")
            }
        }

        // Spell Slots (casters only)
        if (CharacterCalculator.SPELLCASTING_ABILITY.containsKey(draft.characterClass)) {
            item {
                val slots = CharacterCalculator.spellSlotsForClass(draft.characterClass, draft.level)
                ReviewSection("Spell Slots") {
                    if (slots.isEmpty()) {
                        Text("None at this level", color = BrandColors.IvoryDim, fontSize = 13.sp)
                    } else {
                        slots.entries.sortedBy { it.key }.forEach { (lvl, slot) ->
                            ReviewRow("Level $lvl", "${slot.total} slot${if (slot.total != 1) "s" else ""}")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AbilityScoreColumn(label: String, score: Int, mod: Int) {
    val pf = pixelFont()
    val sf = silkFont()
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Text(label, color = BrandColors.IvoryDim, fontFamily = sf, fontSize = 8.sp)
        Text("$score", color = BrandColors.Parchment, fontFamily = pf, fontWeight = FontWeight.Bold, fontSize = 23.sp)
        Text(
            text       = CharacterCalculator.modifierString(score),
            color      = if (mod >= 0) BrandColors.Venom else BrandColors.EmberDim,
            fontFamily = pf,
            fontSize   = 12.sp,
        )
    }
}

@Composable
private fun ReviewSection(title: String, content: @Composable ColumnScope.() -> Unit) {
    val pf = pixelFont()
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(bottom = 12.dp)
            .clip(RoundedCornerShape(14.dp))
            .background(BrandColors.Surface)
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Text(title, color = BrandColors.Parchment, fontFamily = pf, fontWeight = FontWeight.Bold, fontSize = 15.sp)
        HorizontalDivider(color = BrandColors.Slate.copy(alpha = 0.2f), modifier = Modifier.padding(vertical = 4.dp))
        content()
    }
}

@Composable
private fun ReviewRow(label: String, value: String, valueColor: Color = BrandColors.TextPrimary) {
    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(label, color = BrandColors.IvoryDim, fontSize = 14.sp)
        Text(value, color = valueColor, fontSize = 14.sp, fontWeight = FontWeight.Medium)
    }
}
